import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { App } from '@/App';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import '@/styles/global.css';
import '@/styles/design.css';
import '@/styles/site.css';
import '@/styles/pages.css';
import '@/styles/motion.css';
import '@/styles/terminal.css';
import '@/styles/interactions.css';

/**
 * The site previously routed on the hash (`#/platform`). Those URLs are in the
 * wild, and a crawler treats them all as one page, so routing moved to real
 * paths. Anything still arriving on a hash URL is rewritten before the router
 * mounts, which keeps old links working without a redirect round-trip.
 */
function normaliseLegacyHashRoute(): void {
  const { hash, pathname, search } = window.location;
  if (!hash.startsWith('#/')) return;
  const target = hash.slice(1) || '/';
  if (pathname === target) return;
  window.history.replaceState(null, '', target + search);
}

normaliseLegacyHashRoute();

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from the document');

const tree = (
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>
);

// Routes are prerendered to static HTML at build time, so in production there is
// markup to hydrate. `npm run dev` serves an empty shell and renders instead.
if (container.hasChildNodes()) {
  hydrateRoot(container, tree);
} else {
  createRoot(container).render(tree);
}
